import { truncate } from 'lodash';
import * as React from 'react';
import { ComponentPropsWithRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Alert, { AlertContent, AlertTitle } from '../../../core/components/Alert.tsx';
import classnames from '../../../core/utils/classnames.tsx';

const MAX_COLLAPSED_LENGTH = 150;

export const LinkButton = ({ className, ...props }: ComponentPropsWithRef<'button'>) => (
    <button
        type="button"
        className={classnames(
            'font-medium underline underline-offset-2 rounded cursor-pointer hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong',
            className
        )}
        {...props}
    />
);

interface ChecksBannerItemProps {
    message: string;
    children?: React.ReactNode;
}

export const ChecksBannerItem = ({ message, children }: ChecksBannerItemProps) => {
    const { t } = useTranslation('assistance');
    const [isExpanded, setIsExpanded] = useState(false);
    const [firstLine] = message.trim().split(/\r?\n/);
    const collapsedMessage = truncate(firstLine, { length: MAX_COLLAPSED_LENGTH, separator: /\s+/, omission: '…' });
    const isCollapsible = collapsedMessage !== message.trim();

    return (
        <li>
            {isCollapsible && !isExpanded ? collapsedMessage : message}
            {isCollapsible && (
                <>
                    {' '}
                    <LinkButton aria-expanded={isExpanded} onClick={() => setIsExpanded((expanded) => !expanded)}>
                        {t(isExpanded ? 'validationChecks.hideDetails' : 'validationChecks.showDetails')}
                    </LinkButton>
                </>
            )}
            {children}
        </li>
    );
};

interface ChecksBannerProps {
    severity: string;
    title: React.ReactNode;
    // Number of rendered rows, used to switch to a bulleted list when there is more than one.
    itemCount: number;
    listClassName?: string;
    footer?: React.ReactNode;
    children: React.ReactNode;
}

const ChecksBanner = ({ severity, title, itemCount, listClassName, footer, children }: ChecksBannerProps) => (
    <div className="w-full p-6 border-b border-solid border-line-weak">
        <Alert severity={severity} className="flex-col">
            <AlertTitle>{title}</AlertTitle>
            <AlertContent>
                <ul
                    className={classnames('ml-6 flex flex-col gap-1', itemCount > 1 && 'pl-4 list-disc', listClassName)}
                >
                    {children}
                </ul>
                {footer}
            </AlertContent>
        </Alert>
    </div>
);

export default ChecksBanner;
